import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Dropdown, Flex, Tooltip } from "antd";
import {
  BellIcon,
  ExitIcon,
  FilledStar,
  MenuIcon,
  StarIcon,
} from "../../assets/icons";
import MuteIcon from "../../assets/icons/Mute";
import CustomAvatar from "../../Components/CustomAvatar";
import UserStatusBadge from "../../Components/UserStatusBadge";
import { useNavigate, useParams } from "react-router";
import { API_ENDPOINTS } from "../../API/endpoints";
import ApiService from "../../API/apiService";
import { handleApiError, localStorageUtil } from "../../utils/CommonFunctions";
import { useBridgeStore } from "../../store/apis/useBridgeStore";

const DirectMessageListItem = ({
  item,
  getFavListApi,
  getChatListApi,
  hideMoreOptions = false,
  isFavoriteList = false,
  userSelfData,
  onListUpdate,
}) => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const triggerDraftRefresh =  useBridgeStore(store => store.triggerDraftRefresh)
  const triggerSidebarRefresh = useBridgeStore(
    (store) => store.triggerSidebarRefresh
  );
  const triggerThreadsRead = useBridgeStore((store) => store.triggerThreadsRead);

  // Memoized derived values
  const isCurrentUser = useMemo(
    () => item?._id === userSelfData?._id,
    [item?._id, userSelfData?._id]
  );

  const isSelected = useMemo(() => userId === item?._id, [userId, item?._id]);

  // Thread replies are included so a chat with only unread replies still shows as unread
  const unreadTotal = (item?.unseenMessagesCount || 0) + (item?.unreadThreadCount || 0);
  const hasUnreadMessages = unreadTotal > 0;

  // State
  const [isFavorite, setIsFavorite] = useState(item?.isFavorite || false);

  // Update favorite status when item changes
  useEffect(() => {
    setIsFavorite(item?.isFavorite || false);
  }, [item?.isFavorite]);

  // Optimized API call helper
  const refreshLists = useCallback(async () => {
    Promise.all([getChatListApi(), getFavListApi()]);
  }, [isFavoriteList, isFavorite, getChatListApi, getFavListApi]);

  // Mute/Unmute functionality
  const handleMuteToggle = useCallback(
    async (id, shouldMute) => {
      onListUpdate(item._id, { isMutedByMe: shouldMute });

      const endpoint = shouldMute
        ? API_ENDPOINTS.muteUser
        : API_ENDPOINTS.unMuteUser;
      const reqBody = shouldMute
        ? { userIdToMute: id }
        : { userIdToUnmute: id };

      try {
        const response = await ApiService.post(endpoint, reqBody);
        if (response.status === 1) {
          let userDataFromStorage = localStorageUtil.getItem("userData");
          userDataFromStorage.mute_users = response.data.mute_users;
          localStorageUtil.setItem("userData", userDataFromStorage);
        }
      } catch (error) {
        handleApiError(error);
        onListUpdate(item._id, { isMutedByMe: !shouldMute });
      }
    },
    [userSelfData]
  );

  // Read status toggle
  const toggleReadStatus = useCallback(
    async (id, isUnread) => {
      try {
        const endpoint = isUnread
          ? `${API_ENDPOINTS.unSeenMessageApi}/${id}/chat?all=true`
          : `${API_ENDPOINTS.markAsUnread}/${id}`;

        const response = await ApiService.put(endpoint);

        if (response?.status === 1) {
          await refreshLists();
          // Marking as read also clears thread replies, so the Threads badge must re-fetch
          if (isUnread) {
            triggerSidebarRefresh(Date.now(), "threads");
            triggerThreadsRead(id);
          }
        }
      } catch (error) {
        handleApiError(error);
      }
    },
    [refreshLists, triggerSidebarRefresh, triggerThreadsRead]
  );

  // Favorite toggle
  const handleFavoriteToggle = useCallback(
    async (id, shouldFavorite) => {
      const endpoint = shouldFavorite
        ? API_ENDPOINTS.addFavApi
        : API_ENDPOINTS.unFavApi;
      const reqBody = {
        userId: userSelfData?._id,
        favouriteUserId: id,
      };

      try {
        const response = await ApiService.post(endpoint, reqBody);
        if (response?.status === 1) {
          setIsFavorite(shouldFavorite);
          await refreshLists();
        }
      } catch (error) {
        setIsFavorite(!shouldFavorite);
        handleApiError(error);
      }
    },
    [userSelfData?._id, refreshLists]
  );

  // Close conversation
  const closeConversation = useCallback(
    async (id) => {
      const reqBody = {
        userId: userSelfData?._id,
        conversationUserId: id,
        ...(item.isFavorite && { fav: true }),
      };

      try {
        const response = await ApiService.post(
          API_ENDPOINTS.closeConversationApi,
          reqBody
        );
        if (response?.status === 1) {
          const channelId = localStorageUtil.getItem("defaultChannelId");
          if (channelId) {
            navigate(`/elsnertech/channels/${channelId}?default=true`);
          }
          await refreshLists();
          localStorageUtil.removeItem(`draft-${id}`);
          triggerDraftRefresh(Date.now())
        
        }
      } catch (error) {
        handleApiError(error);
      }
    },
    [userSelfData?._id, item?.isFavorite, navigate, refreshLists]
  );

  // Memoized user display data
  const userDisplayData = useMemo(() => {
    const userData = isCurrentUser ? userSelfData : item;
    const username = userData?.username || "";
    const isSelf = isCurrentUser;

    const truncatedName = username;
      // username.length > 20
      //   ? `${username.slice(0, isSelf ? (hasUnreadMessages ? 12 : 16) : (hasUnreadMessages ? 18 : 22))}...`
      //   : username;

    const displayName = isSelf ? `${truncatedName} (you)` : truncatedName;

    const tooltipTitle =
      username.length > 8 ? (isSelf ? `${username} (you)` : username) : null;

    return {
      avatarUrl: userData?.thumbnail_avatarUrl,
      status: userData?.status,
      customStatusEmoji: userData?.custom_status_emoji,
      customStatus: userData?.custom_status,
      displayName,
      tooltipTitle,
    };
  }, [isCurrentUser, userSelfData, item]);

  // Memoized menu items
  const menuItems = useMemo(
    () => [
      {
        key: "sidebarDirectChatddItem-1",
        label: hasUnreadMessages ? "Mark as read" : "Mark as unread",
        icon: <MenuIcon />,
        onClick: () => toggleReadStatus(item?._id, hasUnreadMessages),
      },
      {
        key: "sidebarDirectChatddItem-2",
        label: isFavorite ? "Unfavorite" : "Favorite",
        icon: isFavorite ? <FilledStar /> : <StarIcon />,
        onClick: () => handleFavoriteToggle(item._id, !isFavorite),
      },
      {
        key: "sidebarDirectChatddItem-3",
        onClick: () => handleMuteToggle(item?._id, !item.isMutedByMe),
        label: item.isMutedByMe ? "Unmute Conversation" : "Mute Conversation",
        icon: item.isMutedByMe ? <BellIcon /> : <MuteIcon />,
      },
      { type: "divider" },
      {
        key: "sidebarDirectChatddItem-5",
        label: <div className="text-danger">Close Conversation</div>,
        icon: <ExitIcon />,
        onClick: () => closeConversation(item?._id),
      },
    ],
    [
      hasUnreadMessages,
      isFavorite,
      item?._id,
      item?.isMutedByMe,
      toggleReadStatus,
      handleFavoriteToggle,
      handleMuteToggle,
      closeConversation,
    ]
  );

  // Early return for null item
  if (!item) return null;

  return (
    <Flex
      justify="flex-start"
      align="center"
      gap="8px"
      className={item.isMutedByMe ? "sidebar-mute" : ""}
      role="button"
      tabIndex="0"
    >
      <div className="msg-wrapper">
        <div className="avatar-badge-wrapper">
          <CustomAvatar
            size={25}
            img={userDisplayData.avatarUrl}
            userName={item?.username}
          />
          <UserStatusBadge
            status={userDisplayData.status}
            showStatus={false}
            font_size="11px"
          />
        </div>
        <span className="sider-name-icon-wrapper">
          <Tooltip
            arrowiselected={isSelected}
            arrow={{ pointAtCenter: true }}
            placement="top"
            title={userDisplayData.tooltipTitle}
            className={`${
              hasUnreadMessages
                ? "name sidebarNameStatusIcon highlight"
                : "name sidebarNameStatusIcon"
            }`}
            style={
              hasUnreadMessages ? { fontWeight: "bold", color: "#FFFFFF" } : {}
            }
          >
            {userDisplayData.displayName}
          </Tooltip>
          {userDisplayData.customStatusEmoji && (
            <Tooltip
              placement="top"
              title={userDisplayData.customStatus}
              arrow={false}
            >
              <img
                className="search-icon"
                width={15}
                height={15}
                src={userDisplayData.customStatusEmoji}
                alt="status"
              />
            </Tooltip>
          )}
        </span>
      </div>

      {!hideMoreOptions && (
        <>
          {hasUnreadMessages && <span className="unread-msg-count">
            {unreadTotal}
          </span>}
          <div
            className="mute-icon-wrapper"
            onClick={(e) => e.stopPropagation()}
          >
            {item.isMutedByMe && <MuteIcon />}
            <Dropdown
              menu={{ items: menuItems }}
              className="ec-dropdown sidebar-directchat-dropdown"
              popupRender={(menu) => (
                <div className="ec-dropdown__menu sidebar-directchat-dropdown__menu">
                  {menu}
                </div>
              )}
              trigger={["click"]}
            >
              <Button
                type="text"
                className="options-menu-btn"
                onClick={(e) => e.stopPropagation()}
              >
                <i className="fa-solid fa-ellipsis-vertical"></i>
              </Button>
            </Dropdown>
          </div>
        </>
      )}
    </Flex>
  );
};

export default React.memo(DirectMessageListItem);
